/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `BusinessEventForm` for the Caintra Connect frontend.
 * @module pages/BusinessEventForm
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/BusinessEventForm.module.css";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import BusinessEventRooms from "../components/BusinessEventRooms/index.jsx";
import { getEventAudienceOptions } from "../data/selectOptions";
import { AppContext } from "../context/AppContext";
import {
  generateAssetUrl,
  validateBusinessEventName,
} from "../utils/common.js";
import { canManageBusinessEvents } from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";
import {
  submitEvent,
  initializeEventCreation,
  addParticipatingCompanyToEvent,
  updateParticipatingCompany,
  deleteParticipatingCompany,
  getParticipatingCompanyAdminDetail,
  getParticipatingCompaniesPage,
  getAllBusinessEvents,
  showEventCreatedSuccess,
} from "../logic/businessEvents";
import { Button, CircularProgress, Switch, Backdrop } from "@mui/material";

import { ReactComponent as SaveIcon } from "../assets/icons/CompanyEditPage/SaveIcon.svg";
import { ReactComponent as UploadIcon } from "../assets/icons/Cloud_Upload.svg";
import { ReactComponent as TitleIcon } from "../assets/icons/EventCreation/TitleIcon.svg";
import { ReactComponent as ChevronLeftIcon } from "../assets/icons/EventCreation/ChevronLeftIcon.svg";
import { ReactComponent as SearchBarLeftIcon } from "../assets/icons/SearchBar/SearchBarLeftIcon.svg";
import { ReactComponent as MoreIcon } from "../assets/icons/Chat/MoreIcon.svg";
import { ReactComponent as CheckCircleIcon } from "../assets/icons/BusinessEvents/CheckCircleIcon.svg";

import AddParticipatingCompanyModal from "../modals/AddParticipatingCompany/index.jsx";
import BusinessEventRequirements from "../modals/BusinessEventRequirements/index.jsx";
import ConfirmationModal from "../modals/ConfirmationModal";
import PageSelector from "../components/PageSelector/index.jsx";

import { use, useRef, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import dayjs from "dayjs";
import AssetImage from "../components/AssetImage";

const PARTICIPANT_ROWS_PER_PAGE = 4;
const PARTICIPANT_CARD_MIN_WIDTH = 240;
const PARTICIPANT_CARD_GAP = 16;
const PARTICIPANT_MAX_COLUMNS = 4;

const BusinessEventForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { changePermissions } = use(AppContext);
  const { allowed } = useRequireAccess(canManageBusinessEvents);

  const [formData, setFormData] = useState({
    imagenBanner: null,
    nombre: "",
    fechaInicioEvento: null,
    fechaFinEvento: null,
    audiencia: null,
    descripcion: "",
    fechaDisponibleSocios: null,
    fechaDisponibleNoSocios: null,
    fechaVigenciaCompradores: null,
    fechaLimiteRegistro: null,
    costoSocios: null,
    costoNoSocios: null,
    horaInicio: null,
    horaFin: null,
    duracionCitas: null,
    horaInicioBreak: null,
    horaFinBreak: null,
    duracionBreak: null,
    activo: false,
    preRegistro: false,
    preRegistroTexto: "",
    cupoMaximo: null,
    salas: [],
  });
  const [participatingCompanies, setParticipatingCompanies] = useState([]);
  const [participatingCompanyIds, setParticipatingCompanyIds] = useState([]);
  const [participantsTotalCount, setParticipantsTotalCount] = useState(0);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showError, setShowError] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [emailRequired, setEmailRequired] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(null); // Store the index of the participant with open dropdown
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState("");
  const [participantError, setParticipantError] = useState("");
  const [imageChanged, setImageChanged] = useState(false);
  const [imageFormat, setImageFormat] = useState(null);
  const [imageName, setImageName] = useState(null);
  const [isEditMode, setIsEditMode] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [addParticipantOpen, setAddParticipantOpen] = useState(false);
  const [viewRequirementsOpen, setViewRequirementsOpen] = useState(false);
  const [loadingRequirementsFor, setLoadingRequirementsFor] = useState(null);
  const [allBusinessEvents, setAllBusinessEvents] = useState([]);
  const [nameValidation, setNameValidation] = useState({
    isValid: true,
  });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSlidingUp, setIsSlidingUp] = useState(false);
  const [participantsPage, setParticipantsPage] = useState(1);
  const [participantColumns, setParticipantColumns] = useState(
    PARTICIPANT_MAX_COLUMNS,
  );

  const participantsGridRef = useRef(null);
  const participantPageCursorsRef = useRef({ 1: null });
  const participantsFetchIdRef = useRef(0);
  const dropdownRef = useRef(null);
  const imageRef = useRef(null);
  const deletionDialogRef = useRef(null);
  const onDragEnter = (e) => imageRef.current.classList.add(styles.dragOver);
  const onDragLeave = (e) => imageRef.current.classList.remove(styles.dragOver);
  const onDrop = (e) => imageRef.current.classList.remove(styles.dragOver);

  /** @param {unknown} audiencia */
  const isAudienciaChosen = (audiencia) =>
    audiencia === 0 ||
    audiencia === 1 ||
    audiencia === 2 ||
    audiencia === "0" ||
    audiencia === "1" ||
    audiencia === "2";

  /** @param {unknown} costo */
  const isCostFilled = (costo) => {
    if (costo === null || costo === undefined) {
      return false;
    }
    const s = String(costo).trim();
    if (s === "") {
      return false;
    }
    const n = Number(s);
    return Number.isFinite(n) && n >= 0;
  };

  useEffect(() => {
    if (!allowed) {
      return;
    }
    const initializeComponent = async () => {
      const token = localStorage.getItem("token");

      if (!token || token.trim() === "undefined") {
        logger.info(
          "Error trying to get the token to retrieve the company data.",
        );
        return;
      }

      const userId = localStorage.getItem("userId");

      // Fetch all business events for name validation
      const allEvents = await getAllBusinessEvents(userId, navigate);
      setAllBusinessEvents(allEvents);

      await initializeEventCreation({
        eventId: location.state?.eventData?._id,
        userId,
        changePermissions,
        navigate,
        dayjs,
        setFormData,
        setParticipatingCompanies,
        setIsLoading: setLoading,
        isFromAdmin: true,
        skipParticipatingCompanies: true,
      });
    };

    // Handle location state changes (edit mode)
    if (location.state?.isEditMode && location.state?.eventData) {
      setIsEditMode(true);
      setEventData(location.state.eventData);
    } else {
      setIsEditMode(false);
      setEventData(null);
    }

    initializeComponent();
  }, [allowed, location.state, navigate, location.pathname]);

  // Validate name when allBusinessEvents are loaded or formData.nombre changes
  useEffect(() => {
    if (allBusinessEvents.length > 0 && formData.nombre) {
      validateEventName(formData.nombre);
    }
  }, [allBusinessEvents, formData.nombre]);

  // Fit as many cards per row as the available width allows, so the grid always
  // renders at most PARTICIPANT_ROWS_PER_PAGE rows on any resolution.
  useEffect(() => {
    const grid = participantsGridRef.current;
    if (!grid || typeof ResizeObserver === "undefined") {
      return;
    }

    const updateColumns = () => {
      const width = grid.clientWidth;
      if (!width) {
        return;
      }
      const fittingColumns = Math.floor(
        (width + PARTICIPANT_CARD_GAP) /
          (PARTICIPANT_CARD_MIN_WIDTH + PARTICIPANT_CARD_GAP),
      );
      setParticipantColumns(
        Math.min(PARTICIPANT_MAX_COLUMNS, Math.max(1, fittingColumns)),
      );
    };

    updateColumns();
    const observer = new ResizeObserver(updateColumns);
    observer.observe(grid);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    participantPageCursorsRef.current = { 1: null };
    setParticipantsPage(1);
  }, [participantColumns, debouncedSearchQuery, eventData?._id]);

  const loadParticipatingCompaniesPage = useCallback(
    async ({ eventId, page, limit, search, includeIds }) => {
      const fetchId = ++participantsFetchIdRef.current;
      setParticipantsLoading(true);
      setShowDropdown(null);

      const lastKey =
        page === 1 ? null : (participantPageCursorsRef.current[page] ?? null);

      const data = await getParticipatingCompaniesPage({
        eventId,
        navigate,
        limit,
        lastKey,
        search,
        includeIds,
      });

      if (fetchId !== participantsFetchIdRef.current) {
        return data;
      }

      if (data.items.length === 0 && page > 1) {
        setParticipantsPage(page - 1);
        setParticipantsLoading(false);
        return data;
      }

      setParticipatingCompanies(data.items);
      if (typeof data.totalCount === "number") {
        setParticipantsTotalCount(data.totalCount);
      }
      if (data.companyIds) {
        setParticipatingCompanyIds(data.companyIds);
      }
      if (data.lastEvaluatedKey) {
        participantPageCursorsRef.current[page + 1] = data.lastEvaluatedKey;
      } else {
        delete participantPageCursorsRef.current[page + 1];
      }
      setParticipantsLoading(false);
      return data;
    },
    [navigate],
  );

  useEffect(() => {
    const eventId = eventData?._id || location.state?.eventData?._id;
    if (!eventId) {
      setParticipatingCompanies([]);
      setParticipatingCompanyIds([]);
      setParticipantsTotalCount(0);
      setParticipantsLoading(false);
      return;
    }

    const limit = participantColumns * PARTICIPANT_ROWS_PER_PAGE;
    if (
      participantsPage > 1 &&
      participantPageCursorsRef.current[participantsPage] == null
    ) {
      setParticipantsPage(1);
      return;
    }

    loadParticipatingCompaniesPage({
      eventId,
      page: participantsPage,
      limit,
      search: debouncedSearchQuery,
      includeIds: participantsPage === 1,
    });
  }, [
    eventData?._id,
    location.state?.eventData?._id,
    participantsPage,
    participantColumns,
    debouncedSearchQuery,
    loadParticipatingCompaniesPage,
  ]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(null);
      }
    }

    if (showDropdown !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      logger.info("Selected file: ", { data: file });
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (!allowedTypes.includes(file.type)) {
        setError(t("pages.buyerCompanyForm.logoFileTypeError"));
        setImageChanged(false);
        setFormData((prev) => ({
          ...prev,
          imagenBanner: null,
        }));
        setImageName(null);
        setImageFormat(null);
        return;
      }

      if (file.size > maxSize) {
        setError(t("pages.buyerCompanyForm.logoFileSizeError"));
        setImageChanged(false);
        setFormData((prev) => ({
          ...prev,
          imagenBanner: null,
        }));
        setImageFormat(null);
        setImageName(null);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        logger.info("e.target.result: ", { data: e.target.result });
        setImageChanged(true);
        setFormData((prev) => ({
          ...prev,
          imagenBanner: e.target.result,
        }));
        setImageName(file.name);
        setImageFormat(file.type);
        setHasChanges(true);
      };
      reader.readAsDataURL(file);
    } else {
      setImageChanged(false);
      setFormData((prev) => ({
        ...prev,
        imagenBanner: null,
      }));
      setImageFormat(null);
      setImageName(null);
      logger.error(
        "Invalid file type. Please upload a JPEG, PNG, or WEBP image.",
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowError(false);

    try {
      const eventToSubmit = {
        ...formData,
        nombreImagen: imageChanged ? imageName : null,
        formatoImagen: imageChanged ? imageFormat : null,
        correoRequerido: emailRequired,
        userId: localStorage.getItem("userId"),
        fechaInicioEvento: dayjs(formData.fechaInicioEvento).format(
          "YYYY-MM-DD",
        ),
        fechaFinEvento: dayjs(formData.fechaFinEvento).format("YYYY-MM-DD"),
        fechaDisponibleSocios: formData.fechaDisponibleSocios
          ? dayjs(formData.fechaDisponibleSocios)
              .hour(0)
              .minute(0)
              .second(0)
              .utc("z")
              .toISOString()
          : null,
        fechaDisponibleNoSocios: formData.fechaDisponibleNoSocios
          ? dayjs(formData.fechaDisponibleNoSocios)
              .hour(0)
              .minute(0)
              .second(0)
              .utc("z")
              .toISOString()
          : null,
        fechaVigenciaCompradores: formData.fechaVigenciaCompradores
          ? dayjs(formData.fechaVigenciaCompradores)
              .hour(23)
              .minute(59)
              .second(59)
              .utc("z")
              .toISOString()
          : null,
        fechaLimiteRegistro: formData.fechaLimiteRegistro
          ? dayjs(formData.fechaLimiteRegistro)
              .hour(23)
              .minute(59)
              .second(59)
              .utc("z")
              .toISOString()
          : null,
        fechaDeCreacion: dayjs().utc("z").toISOString(),
      };
      const result = await submitEvent(
        eventToSubmit,
        eventData?._id,
        navigate,
        setSaving,
        setEventData,
        setHasChanges,
      );
      if (result?.ok && result.wasCreate) {
        setIsEditMode(true);
        setImageChanged(false);
        if (result.event) {
          setAllBusinessEvents((prev) => [...prev, result.event]);
        }
        showEventCreatedSuccess(setShowSuccessMessage, setIsSlidingUp);
      }
    } catch (error) {
      logger.error("Error submitting event:", { err: sanitizeError(error) });
      setShowError(true);
    }
  };

  const handleConfirmDelete = async (participant) => {
    const effectiveParticipant = participant || selectedParticipant;
    if (!effectiveParticipant) {
      return;
    }

    try {
      setIsDeleting(true);
      const result = await deleteParticipatingCompany(
        effectiveParticipant,
        navigate,
      );
      if (result) {
        const eventId = eventData?._id || location.state?.eventData?._id;
        if (eventId) {
          participantPageCursorsRef.current = { 1: null };
          setParticipantsPage(1);
          await loadParticipatingCompaniesPage({
            eventId,
            page: 1,
            limit: participantColumns * PARTICIPANT_ROWS_PER_PAGE,
            search: debouncedSearchQuery,
            includeIds: true,
          });
        }
        logger.info("Participant deleted successfully");
      } else {
        logger.error("Failed to delete participant");
      }
    } catch (error) {
      logger.error("Error deleting participant:", {
        err: sanitizeError(error),
      });
    } finally {
      setIsDeleting(false);
      setSelectedParticipant(null);
    }
  };

  const handleCloseDeleteConfirmation = () => {
    setDeleteConfirmationOpen(false);
    setSelectedParticipant(null);
    setIsDeleting(false);
  };

  const deleteParticipant = async (event, participant, index) => {
    event.preventDefault();
    event.stopPropagation();
    logger.info("Delete participant: ", { data: [participant, index] });
    if (!participant.cantidadRequerimientos) {
      await handleConfirmDelete(participant);
    } else {
      setSelectedParticipant(participant);
      setDeleteConfirmationOpen(true);
    }
  };

  const editParticipant = (event, participant, index) => {
    event.preventDefault();
    event.stopPropagation();
    setShowDropdown(null);
    // The modal loads the full detail itself so it can show a single,
    // uninterrupted spinner instead of one on each side of the open.
    setSelectedParticipant(participant);
    setAddParticipantOpen(true);
  };

  const checkSaveDisabled = () => {
    return (
      !hasChanges ||
      saving ||
      loading ||
      !formData.nombre ||
      !formData.fechaInicioEvento ||
      !formData.fechaFinEvento ||
      !isAudienciaChosen(formData.audiencia) ||
      !formData.descripcion ||
      !formData.fechaDisponibleSocios ||
      !formData.fechaDisponibleNoSocios ||
      !formData.fechaVigenciaCompradores ||
      !formData.fechaLimiteRegistro ||
      !isCostFilled(formData.costoSocios) ||
      !isCostFilled(formData.costoNoSocios) ||
      !formData.horaInicio ||
      !formData.horaFin ||
      !formData.duracionCitas ||
      !formData.horaInicioBreak ||
      !formData.horaFinBreak ||
      !formData.imagenBanner ||
      !nameValidation.isValid ||
      !formData.cupoMaximo ||
      (formData.preRegistro && !formData.preRegistroTexto?.trim())
    );
  };

  // Validate event name in real-time
  const validateEventName = (eventName) => {
    if (!eventName || allBusinessEvents.length === 0) {
      setNameValidation({
        isValid: true,
      });
      return;
    }

    const validation = validateBusinessEventName(
      eventName,
      allBusinessEvents,
      eventData?._id, // Pass current event ID for edit mode
    );
    setNameValidation(validation);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate event name in real-time
    if (name === "nombre") {
      validateEventName(value);
    }

    if (
      name === "fechaInicioEvento" ||
      name === "fechaFinEvento" ||
      name === "horaInicio" ||
      name === "horaFin" ||
      name === "ubicacion"
    ) {
      setEmailRequired(true);
    }
    setHasChanges(true);
  };

  const handleSelectChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      [selectedOption.name]: selectedOption.value,
    }));
    setHasChanges(true);
  };

  /** @param {"socios"|"noSocios"} which */
  const handleAudienciaCheckboxChange = (which) => {
    setFormData((prev) => {
      const aud = prev.audiencia;
      const sociosWas = aud === 0 || aud === "0" || aud === 2 || aud === "2";
      const noSociosWas = aud === 1 || aud === "1" || aud === 2 || aud === "2";
      const socios = which === "socios" ? !sociosWas : sociosWas;
      const noSocios = which === "noSocios" ? !noSociosWas : noSociosWas;
      let audiencia = null;
      if (socios && noSocios) {
        audiencia = 2;
      } else if (socios) {
        audiencia = 0;
      } else if (noSocios) {
        audiencia = 1;
      }
      return { ...prev, audiencia };
    });
    setHasChanges(true);
  };

  const toggleDropdown = (event, index) => {
    event.stopPropagation();
    setShowDropdown(showDropdown === index ? null : index);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setParticipantsPage(1);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setDebouncedSearchQuery(searchQuery);
      setParticipantsPage(1);
    }
  };

  const handleAddRequirements = async (participant) => {
    setLoadingRequirementsFor(participant.idEmpresaCompradora);
    try {
      const companyDetails = await getParticipatingCompanyAdminDetail(
        participant.idEvento,
        participant.idEmpresaCompradora,
        navigate,
      );
      if (!companyDetails) {
        logger.error("Failed to load participating company requirements");
        return;
      }
      setSelectedParticipant(companyDetails);
      setViewRequirementsOpen(true);
    } finally {
      setLoadingRequirementsFor(null);
    }
  };

  const participantsPerPage = participantColumns * PARTICIPANT_ROWS_PER_PAGE;
  const participantsTotalPages = Math.max(
    1,
    Math.ceil(participantsTotalCount / participantsPerPage) || 1,
  );
  const currentParticipantsPage = Math.min(
    participantsPage,
    participantsTotalPages,
  );

  const handleParticipantsPageChange = (direction) => {
    setShowDropdown(null);
    setParticipantsPage((current) => {
      if (direction === "next") {
        return Math.min(current + 1, participantsTotalPages);
      }
      return Math.max(current - 1, 1);
    });
  };

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        {/* Back navigation */}
        <div className={styles.backNavigation}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <ChevronLeftIcon />
            <span>{t("common.goBack")}</span>
          </button>
        </div>

        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <TitleIcon className={styles.titleIcon} />
            <h1 className={styles.title}>
              {t("pages.businessEventForm.header")}
            </h1>
          </div>
        </div>
      </div>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={(isEditMode && loading) || loadingRequirementsFor !== null}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>

      <div className={styles.formContainer}>
        <form onSubmit={handleSubmit} className={styles.eventForm}>
          <div className={styles.formGroups}>
            <div className={styles.formGroupOne}>
              <div className={styles.formGroupOneLeftStack}>
                <div className={styles.imageContainer}>
                  <label className={styles.label}>
                    {t("pages.businessEventForm.eventBanner")}
                  </label>
                  <div
                    ref={imageRef}
                    className={styles.dropFileInput}
                    onDragEnter={onDragEnter}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                  >
                    {isEditMode || formData.imagenBanner ? (
                      <AssetImage
                        className={styles.uploadedImage}
                        src={generateAssetUrl(formData.imagenBanner)}
                        alt={t("common.bannerAlt")}
                      />
                    ) : (
                      <div className={styles.uploadContainer}>
                        <UploadIcon className={styles.uploadIcon} />
                        <span className={styles.uploadTitle}>
                          {t("common.drag&drop")}
                        </span>
                        <span className={styles.uploadSubtitle}>
                          {t("common.yourImage")}{" "}
                          <span
                            style={{ color: "#1D5AE8" }}
                            className={styles.uploadSubtitle}
                          >
                            {t("common.browseFile")}
                          </span>
                        </span>
                      </div>
                    )}
                    <input
                      className={styles.imageInput}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </div>
                </div>
                <div className={styles.audienciaBelowBanner}>
                  <label className={styles.label}>
                    {t("pages.businessEventForm.audience")}
                  </label>
                  <div className={styles.audienciaCheckboxRow}>
                    <label className={styles.audienciaCheckboxLabel}>
                      <input
                        type="checkbox"
                        checked={
                          formData.audiencia === 0 ||
                          formData.audiencia === "0" ||
                          formData.audiencia === 2 ||
                          formData.audiencia === "2"
                        }
                        onChange={() => handleAudienciaCheckboxChange("socios")}
                      />
                      <span>{t("pages.businessEventForm.audienceSocios")}</span>
                    </label>
                    <label className={styles.audienciaCheckboxLabel}>
                      <input
                        type="checkbox"
                        checked={
                          formData.audiencia === 1 ||
                          formData.audiencia === "1" ||
                          formData.audiencia === 2 ||
                          formData.audiencia === "2"
                        }
                        onChange={() =>
                          handleAudienciaCheckboxChange("noSocios")
                        }
                      />
                      <span>
                        {t("pages.businessEventForm.audienceNonMembers")}
                      </span>
                    </label>
                  </div>
                </div>
                <div className={styles.activoBelowBanner}>
                  <label className={styles.label}>
                    {t("pages.businessEventForm.activateEvent")}
                  </label>
                  <Switch
                    checked={Boolean(formData.activo)}
                    onChange={(e) => {
                      setHasChanges(true);
                      setFormData((prev) => ({
                        ...prev,
                        activo: e.target.checked,
                      }));
                    }}
                  />
                </div>
              </div>

              <div className={styles.detailsContainer}>
                <div className={`${styles.formRow} ${styles.rowEventName}`}>
                  <div className={styles.nameCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.eventName")}
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleInputChange}
                      className={styles.input}
                      style={{
                        borderColor:
                          showError && !formData.nombre
                            ? "red"
                            : !nameValidation.isValid
                              ? "red"
                              : "#909092",
                      }}
                      placeholder={t(
                        "pages.businessEventForm.eventNamePlaceholder",
                      )}
                    />
                    {/* Name validation feedback */}
                    {!nameValidation.isValid && (
                      <div
                        className={styles.validationMessage}
                        style={{
                          color: "red",
                          fontSize: "12px",
                          marginTop: "4px",
                          fontWeight: "500",
                        }}
                      >
                        {t("pages.businessEventForm.duplicateEventNameError")}
                      </div>
                    )}
                  </div>
                </div>

                <div className={`${styles.formRow} ${styles.rowEventType}`}>
                  <div className={styles.eventDateCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.eventStartDate")}
                    </label>
                    <input
                      type="date"
                      name="fechaInicioEvento"
                      placeholder={t("common.dateFormatPlaceholder")}
                      className={styles.input}
                      style={{
                        borderColor:
                          showError && !formData.fechaInicioEvento
                            ? "red"
                            : "#909092",
                      }}
                      value={formData.fechaInicioEvento}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className={styles.dateDivider} />
                  <div className={styles.eventDateCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.eventEndDate")}
                    </label>
                    <input
                      type="date"
                      name="fechaFinEvento"
                      placeholder={t("common.dateFormatPlaceholder")}
                      className={styles.input}
                      style={{
                        borderColor:
                          showError && !formData.fechaFinEvento
                            ? "red"
                            : "#909092",
                      }}
                      value={formData.fechaFinEvento}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.descriptionCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.description")}
                    </label>
                    <textarea
                      name="descripcion"
                      value={formData.descripcion}
                      onChange={handleInputChange}
                      className={styles.textarea}
                      style={{
                        borderColor:
                          showError && !formData.descripcion
                            ? "red"
                            : "#909092",
                      }}
                      placeholder={t(
                        "pages.businessEventForm.descriptionPlaceholder",
                      )}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.eventDateCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.membersDate")}
                    </label>
                    <input
                      type="date"
                      name="fechaDisponibleSocios"
                      placeholder={t("common.dateFormatPlaceholder")}
                      className={styles.input}
                      style={{
                        borderColor:
                          showError && !formData.fechaDisponibleSocios
                            ? "red"
                            : "#909092",
                      }}
                      value={formData.fechaDisponibleSocios || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className={styles.eventDateCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.nonMembersDate")}
                    </label>
                    <input
                      type="date"
                      name="fechaDisponibleNoSocios"
                      placeholder={t("common.dateFormatPlaceholder")}
                      className={styles.input}
                      style={{
                        borderColor:
                          showError && !formData.fechaDisponibleNoSocios
                            ? "red"
                            : "#909092",
                      }}
                      value={formData.fechaDisponibleNoSocios || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.eventDateCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.dueDateBuyer")}
                    </label>
                    <input
                      type="date"
                      name="fechaVigenciaCompradores"
                      placeholder={t("common.dateFormatPlaceholder")}
                      className={styles.input}
                      style={{
                        borderColor:
                          showError && !formData.fechaVigenciaCompradores
                            ? "red"
                            : "#909092",
                      }}
                      value={formData.fechaVigenciaCompradores || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className={styles.eventDateCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.dueDateRegistration")}
                    </label>
                    <input
                      type="date"
                      name="fechaLimiteRegistro"
                      placeholder={t("common.dateFormatPlaceholder")}
                      className={styles.input}
                      style={{
                        borderColor:
                          showError && !formData.fechaLimiteRegistro
                            ? "red"
                            : "#909092",
                      }}
                      value={formData.fechaLimiteRegistro || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.costCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.membersCost")}
                    </label>
                    <input
                      type="number"
                      name="costoSocios"
                      min={0}
                      step={"any"}
                      value={formData.costoSocios}
                      style={{
                        borderColor:
                          showError && !formData.costoSocios
                            ? "red"
                            : "#909092",
                      }}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder={t("pages.businessEventForm.costPlaceholder")}
                    />
                  </div>
                  <div className={styles.costCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.nonMembersCost")}
                    </label>
                    <input
                      type="number"
                      name="costoNoSocios"
                      min={0}
                      step={"any"}
                      value={formData.costoNoSocios}
                      style={{
                        borderColor:
                          showError && !formData.costoNoSocios
                            ? "red"
                            : "#909092",
                      }}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder={t("pages.businessEventForm.costPlaceholder")}
                    />
                  </div>
                  <div className={styles.timeCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.maximumCapacity")}
                    </label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      name="cupoMaximo"
                      value={formData.cupoMaximo}
                      onChange={handleInputChange}
                      className={styles.input}
                      style={{
                        borderColor:
                          showError && !formData.cupoMaximo ? "red" : "#909092",
                      }}
                      placeholder={t(
                        "pages.businessEventForm.maximumCapacityPlaceholder",
                      )}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.timeCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.startTime")}
                    </label>
                    <input
                      type="time"
                      name="horaInicio"
                      value={formData.horaInicio}
                      onChange={handleInputChange}
                      className={styles.input}
                      style={{
                        borderColor:
                          showError && !formData.horaInicio ? "red" : "#909092",
                      }}
                      placeholder={t("common.timeExamplePlaceholder")}
                    />
                  </div>
                  <div className={styles.timeCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.endTime")}
                    </label>
                    <input
                      type="time"
                      name="horaFin"
                      value={formData.horaFin}
                      onChange={handleInputChange}
                      className={styles.input}
                      style={{
                        borderColor:
                          showError && !formData.horaFin ? "red" : "#909092",
                      }}
                      placeholder={t("common.timeExamplePlaceholder")}
                    />
                  </div>
                  <div className={styles.durationCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.duration")}
                    </label>
                    <div className={styles.durationInputContainer}>
                      <input
                        type="text"
                        name="duracionCitas"
                        pattern="([0-9]|1[0-9]|2[0-4]):[0-5][0-9]"
                        value={formData.duracionCitas || "0:00"}
                        onChange={handleInputChange}
                        className={styles.input}
                        style={{
                          borderColor:
                            showError && !formData.duracionCitas
                              ? "red"
                              : "#909092",
                        }}
                        placeholder={t("common.durationPlaceholder")}
                      />
                      <span className={styles.inputLabel}>{t("common.hoursUnit")}</span>
                    </div>
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.timeCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.breakStartTime")}
                    </label>
                    <input
                      type="time"
                      name="horaInicioBreak"
                      value={formData.horaInicioBreak}
                      onChange={handleInputChange}
                      className={styles.input}
                      style={{
                        borderColor:
                          showError && !formData.horaInicioBreak
                            ? "red"
                            : "#909092",
                      }}
                      placeholder={t("common.timeExamplePlaceholder")}
                    />
                  </div>
                  <div className={styles.timeCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.breakEndTime")}
                    </label>
                    <input
                      type="time"
                      name="horaFinBreak"
                      value={formData.horaFinBreak}
                      onChange={handleInputChange}
                      className={styles.input}
                      style={{
                        borderColor:
                          showError && !formData.horaFinBreak
                            ? "red"
                            : "#909092",
                      }}
                      placeholder={t("common.timeExamplePlaceholder")}
                    />
                  </div>
                  <div className={styles.durationCol}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.breakDuration")}
                    </label>
                    <div className={styles.durationInputContainer}>
                      <input
                        type="text"
                        name="duracionBreak"
                        pattern="([0-9]|1[0-9]|2[0-4]):[0-5][0-9]"
                        value={formData.duracionBreak || "0:00"}
                        onChange={handleInputChange}
                        className={styles.input}
                        style={{
                          borderColor:
                            showError && !formData.duracionBreak
                              ? "red"
                              : "#909092",
                        }}
                        placeholder={t("common.durationPlaceholder")}
                      />
                      <span className={styles.inputLabel}>{t("common.hoursUnit")}</span>
                    </div>
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formColTwo}>
                    <label className={styles.label}>
                      {t("pages.businessEventForm.preRegistro")}
                    </label>
                    <Switch
                      checked={formData.preRegistro}
                      onChange={(e) => {
                        setHasChanges(true);
                        setFormData((prev) => ({
                          ...prev,
                          preRegistro: e.target.checked,
                          preRegistroTexto: e.target.checked
                            ? prev.preRegistroTexto
                            : "",
                        }));
                      }}
                    />
                  </div>
                  {formData.preRegistro && (
                    <div className={styles.audienceCol}>
                      <label className={styles.label}>
                        {t("pages.businessEventForm.preRegistroTexto")}
                      </label>
                      <textarea
                        name="preRegistroTexto"
                        value={formData.preRegistroTexto || ""}
                        onChange={handleInputChange}
                        className={`${styles.textarea} ${styles.preRegistroTextoInput}`}
                        style={{
                          borderColor:
                            showError && !formData.preRegistroTexto?.trim()
                              ? "red"
                              : "#909092",
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.formGroupTwo}>
              <div className={styles.sectionTitle}>
                <span>
                  {t("pages.businessEventForm.participatingCompanies")}
                </span>
              </div>
              <div className={styles.participantsHeader}>
                <div className={styles.participantsActions}>
                  <div className={styles.searchBar}>
                    <div className={styles.searchBarLeftIcon}>
                      <SearchBarLeftIcon />
                    </div>
                    <input
                      placeholder={t("pages.businessEventForm.searchText")}
                      type="search"
                      value={searchQuery}
                      onChange={handleSearch}
                      onKeyPress={handleKeyPress}
                    />
                  </div>
                  <button
                    type="button"
                    className={styles.newEventButton}
                    disabled={eventData === null || eventData === undefined}
                    onClick={() => setAddParticipantOpen(true)}
                  >
                    {t("pages.businessEventForm.addCompany")}
                  </button>
                </div>
              </div>
              <div className={styles.participantsListContainer}>
                <div
                  ref={participantsGridRef}
                  className={styles.participantsGrid}
                  style={{ "--participant-columns": participantColumns }}
                >
                  {participantsLoading &&
                    participatingCompanies.length === 0 && (
                      <div className={styles.noResults}>
                        <CircularProgress size={28} sx={{ color: "#4eabe7" }} />
                      </div>
                    )}
                  {!participantsLoading &&
                    participatingCompanies.length === 0 && (
                      <div className={styles.noResults}>
                        <p>{t("pages.businessEventForm.noCompanies")}</p>
                      </div>
                    )}
                  {participatingCompanies.map((participant, index) => (
                    <div
                      key={participant.idEmpresaCompradora ?? index}
                      className={styles.participantCard}
                    >
                      <div className={styles.participantHeader}>
                        <span className={styles.participantName}>
                          {participant.nombreComercial}
                        </span>
                        <button
                          type="button"
                          className={styles.moreButton}
                          disabled={
                            loadingRequirementsFor ===
                            participant.idEmpresaCompradora
                          }
                          onClick={(event) => toggleDropdown(event, index)}
                        >
                          <MoreIcon />
                        </button>
                        {showDropdown === index && (
                          <div
                            className={styles.chatHeaderDropdownMenu}
                            ref={dropdownRef}
                          >
                            <button
                              type="button"
                              className={styles.chatHeaderDropdownItem}
                              onClick={(event) =>
                                editParticipant(event, participant, index)
                              }
                            >
                              {t("pages.businessEventForm.edit")}
                            </button>
                            <div
                              className={styles.chatHeaderDropdownDivider}
                            ></div>
                            {participant.puedeEliminarse && (
                              <button
                                type="button"
                                className={`${styles.chatHeaderDropdownItem} ${styles.rejectItem}`}
                                onClick={(event) =>
                                  deleteParticipant(event, participant, index)
                                }
                              >
                                {t("pages.businessEventForm.delete")}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div className={styles.participantDetails}>
                        <span className={styles.detailLabel}>
                          {participant.cantidadEjecutivosCompradores ??
                            participant.ejecutivosCompradores?.length ??
                            0}{" "}
                          {t("pages.businessEventForm.executives")}
                        </span>
                        <span className={styles.detailLabel}>
                          {participant.cantidadRequerimientos ??
                            participant.requerimientos?.length ??
                            0}{" "}
                          {t("pages.businessEventForm.requirements")}
                        </span>
                        <button
                          type="button"
                          className={styles.addRequirementsButton}
                          disabled={
                            loadingRequirementsFor ===
                            participant.idEmpresaCompradora
                          }
                          onClick={() => handleAddRequirements(participant)}
                        >
                          {loadingRequirementsFor ===
                          participant.idEmpresaCompradora ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            t("pages.businessEventForm.createRequirements")
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {participantsTotalCount > 0 && (
                  <div className={styles.participantsFooter}>
                    <PageSelector
                      currentPage={currentParticipantsPage}
                      totalPages={participantsTotalPages}
                      onPageChange={handleParticipantsPageChange}
                      disabled={{
                        prev: participantsLoading,
                        next: participantsLoading,
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            <BusinessEventRooms
              roomsData={formData.salas}
              maxSpaces={formData.maxCitasConcurrentesPorDia}
              onRoomsChange={(updatedRooms) => {
                setFormData((prev) => ({
                  ...prev,
                  salas: updatedRooms,
                }));
                setHasChanges(true);
              }}
            />
          </div>
          <div className={styles.saveSectionContainer}>
            <div className={styles.saveSectionContent}>
              <div className={styles.messageContainer}>
                {t("pages.businessEventForm.publishInstructions")}
              </div>
              <Button
                type="submit"
                variant="contained"
                className={styles.saveButton}
                disabled={checkSaveDisabled()}
                startIcon={loading ? null : <SaveIcon />}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("pages.businessEventForm.publishEvent")
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>

      {addParticipantOpen && (
        <AddParticipatingCompanyModal
          open={addParticipantOpen}
          selectedParticipant={selectedParticipant}
          eventId={eventData?._id}
          eventData={eventData}
          participatingCompanies={participatingCompanyIds.map((id) => ({
            idEmpresaCompradora: id,
          }))}
          error={participantError}
          onCancel={() => {
            setSelectedParticipant(null);
            setParticipantError(null);
            setAddParticipantOpen(false);
          }}
          onSuccess={async (company, isEditMode) => {
            const newCompany = {
              idEvento: company.idEvento,
              idEmpresaCompradora: company.empresa._id,
              tiempoBreak: company.tiempoBreak,
              nombreComercial: company.empresa.nombreComercial,
              ejecutivosCompradores: company.ejecutivosCompradores || [],
            };
            // The list is server-paginated and sorted, so it is refetched below
            // instead of patched locally.
            if (isEditMode) {
              await updateParticipatingCompany(
                newCompany,
                null,
                setParticipantError,
                navigate,
              );
            } else {
              await addParticipatingCompanyToEvent(
                newCompany,
                null,
                setParticipantError,
                navigate,
              );
            }
            const eventId = eventData?._id || location.state?.eventData?._id;
            if (eventId) {
              participantPageCursorsRef.current = { 1: null };
              setParticipantsPage(1);
              await loadParticipatingCompaniesPage({
                eventId,
                page: 1,
                limit: participantColumns * PARTICIPANT_ROWS_PER_PAGE,
                search: debouncedSearchQuery,
                includeIds: true,
              });
            }
            setSelectedParticipant(null);
            setParticipantError(null);
            setAddParticipantOpen(false);
          }}
        />
      )}

      {viewRequirementsOpen && (
        <BusinessEventRequirements
          open={viewRequirementsOpen}
          participant={selectedParticipant}
          onCancel={(participant, requirements) => {
            // Keep list state summary-only: refresh the count, not the bodies
            setParticipatingCompanies((prev) =>
              prev.map((comp) =>
                comp.idEmpresaCompradora === participant.idEmpresaCompradora
                  ? { ...comp, cantidadRequerimientos: requirements.length }
                  : comp,
              ),
            );
            setSelectedParticipant(null);
            setViewRequirementsOpen(false);
          }}
        />
      )}

      {deleteConfirmationOpen && (
        <ConfirmationModal
          dialogRef={deletionDialogRef}
          open={deleteConfirmationOpen}
          onClose={handleCloseDeleteConfirmation}
          onConfirm={handleConfirmDelete}
          title={t("components.confirmationModal.title")}
          message={t("pages.businessEventForm.deleteCompanyWarning")}
          confirmButtonText={t("components.confirmationModal.confirm")}
          cancelButtonText={t("components.confirmationModal.cancel")}
          isLoading={isDeleting}
        />
      )}

      {showSuccessMessage && (
        <div
          className={`${styles.successMessage} ${
            isSlidingUp ? styles.slideUp : ""
          }`}
        >
          <CheckCircleIcon className={styles.successMessageIcon} />
          {t("pages.businessEvents.creationMessage")}
        </div>
      )}
    </div>
  );
};

export default BusinessEventForm;
